import { CANCEL_CODE, type IErrorData } from '@/utils/getErrorData';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getOrdersThunk } from './ordersThunks';
import { IOrder } from '@/types/IOrder';
import { type TOrdersStatus } from '@/types/IOrdersStatus';

export interface IOrdersState {
  orders: IOrder[];
  total: number;
  status: TOrdersStatus;
  error: IErrorData | null;
  liveOrders: IOrder[];
}

const initialState: IOrdersState = {
  orders: [],
  total: 0,
  status: 'idle',
  error: null,
  liveOrders: []
};

const orders = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    resetOrders(state) {
      state.orders = initialState.orders;
      state.total = initialState.total;
      state.status = initialState.status;
      state.error = initialState.error;
      state.liveOrders = initialState.liveOrders;
    },
    addLiveOrder(state, action: PayloadAction<IOrder>) {
      const incoming = action.payload;
      const existingIndex = state.liveOrders.findIndex(
        (order) => order.id === incoming.id
      );

      if (existingIndex !== -1) {
        state.liveOrders[existingIndex] = incoming;
        return;
      }

      // Держим эфемерный слой в порядке id DESC (как серверная выдача),
      // чтобы заказы, пришедшие не по порядку, вставали на свои места.
      const insertAt = state.liveOrders.findIndex(
        (order) => order.id < incoming.id
      );

      if (insertAt === -1) {
        state.liveOrders.push(incoming);
      } else {
        state.liveOrders.splice(insertAt, 0, incoming);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrdersThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        // Любой запрос списка возвращает нас к «правде бэка» —
        // эфемерный realtime-слой при этом сбрасывается.
        state.liveOrders = [];
      })
      .addCase(getOrdersThunk.fulfilled, (state, action) => {
        state.orders = action.payload.items;
        state.total = action.payload.total;
        state.status = 'success';
        state.error = null;
      })
      .addCase(getOrdersThunk.rejected, (state, action) => {
        if (action.payload?.code === CANCEL_CODE) {
          return;
        }

        state.status = 'error';
        state.error = action.payload ?? {
          message: 'Ошибка при получении заказов'
        };
      });
  }
});

export const { resetOrders, addLiveOrder } = orders.actions;
export default orders.reducer;
